/**
 * @module features/devices/components/DeviceInspectorDrawer
 * @description Slide-over / bottom sheet on mobile inspector: identity, status,
 * type-specific attributes, lifecycle actions, firmware timeline, and audit trail.
 * Loads detail (with history) lazily and handles all 4 UI states.
 * On mobile (<640px), opens as an edge-to-edge bottom sheet with a grab handle indicator.
 * On desktop/tablet (>=640px), slides out from the right (480px).
 */
import * as React from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Activity, AlertCircle, Check, Copy, Loader2, Pencil, RotateCcw, Trash2 } from "lucide-react";
import { Drawer } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AppError } from "@/lib/api-client";
import { useDeviceDetail } from "../api/useDeviceDetail";
import { useReactivateDevice, useUpdateDevice } from "../api/useDeviceMutations";
import { DeviceItem, DeviceStatus, DeviceUpdateInput, OS_HASH_REGEX } from "../types";
import { formatDateTime } from "../utils/format";
import { DeviceStatusBadge, statusLabel } from "./DeviceStatusBadge";
import { DecommissionDialog } from "./DecommissionDialog";
import { FirmwareTimeline } from "./FirmwareTimeline";
import { DeviceCredentialsCard } from "./DeviceCredentialsCard";

/** Mirrors backend ALLOWED_TRANSITIONS; the server remains the authority. */
const NEXT_STATUSES: Record<DeviceStatus, DeviceStatus[]> = {
  PROVISIONED: ["ACTIVE", "MAINTENANCE"],
  ACTIVE: ["OFFLINE", "MAINTENANCE"],
  OFFLINE: ["ACTIVE", "MAINTENANCE"],
  MAINTENANCE: ["ACTIVE", "OFFLINE"],
  DECOMMISSIONED: [],
};

interface EditValues {
  device_name: string;
  status: DeviceStatus;
  os_name: string;
  os_version: string;
  os_hash: string;
  notes: string;
}

interface DeviceInspectorDrawerProps {
  deviceId: string | null;
  onClose: () => void;
  onEdit?: (device: DeviceItem) => void;
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-border/50 last:border-0">
      <dt className="text-xs text-muted-foreground shrink-0">{label}</dt>
      <dd className="text-xs text-foreground text-right min-w-0 break-all">{children}</dd>
    </div>
  );
}

export function DeviceInspectorDrawer({ deviceId, onClose, onEdit }: DeviceInspectorDrawerProps) {
  const navigate = useNavigate();
  const { data: device, isLoading, isError, error, refetch } = useDeviceDetail(deviceId);
  const [editing, setEditing] = React.useState(false);
  const [decommissionOpen, setDecommissionOpen] = React.useState(false);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [copiedMac, setCopiedMac] = React.useState(false);
  const [copiedId, setCopiedId] = React.useState(false);
  const updateMutation = useUpdateDevice();
  const reactivateMutation = useReactivateDevice();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, dirtyFields },
  } = useForm<EditValues>();

  React.useEffect(() => {
    if (device) {
      reset({
        device_name: device.device_name,
        status: device.status,
        os_name: device.display_module?.os_name ?? "",
        os_version: device.display_module?.os_version ?? "",
        os_hash: device.display_module?.os_hash ?? "",
        notes: "",
      });
      setEditing(false);
    }
  }, [device, reset]);

  const decommissioned = device?.status === "DECOMMISSIONED";

  const copyText = (text: string, type: "mac" | "id") => {
    try {
      navigator.clipboard.writeText(text);
      if (type === "mac") {
        setCopiedMac(true);
        setTimeout(() => setCopiedMac(false), 1500);
      } else {
        setCopiedId(true);
        setTimeout(() => setCopiedId(false), 1500);
      }
    } catch {
      /* clipboard may be unavailable; ignore */
    }
  };

  const onSave = async (values: EditValues) => {
    if (!device) return;
    setActionError(null);
    const input: DeviceUpdateInput = {};
    (["device_name", "status", "os_name", "os_version", "os_hash"] as const).forEach((key) => {
      if (dirtyFields[key]) (input as Record<string, string>)[key] = values[key].trim();
    });
    if ((input.os_version || input.os_hash) && values.notes.trim()) {
      input.notes = values.notes.trim();
    }
    if (Object.keys(input).length === 0) {
      setEditing(false);
      return;
    }
    try {
      await updateMutation.mutateAsync({ id: device.id, input });
      setEditing(false);
    } catch (err) {
      setActionError(
        err instanceof AppError ? `${err.errorDetails} (Request ID: ${err.requestId})` : "Failed to save changes."
      );
    }
  };

  const onReactivate = async () => {
    if (!device) return;
    setActionError(null);
    try {
      await reactivateMutation.mutateAsync({ id: device.id });
    } catch (err) {
      setActionError(err instanceof AppError ? err.errorDetails : "Failed to reactivate the device.");
    }
  };

  const nextStatuses = device ? NEXT_STATUSES[device.status] : [];

  return (
    <>
      <Drawer
        open={Boolean(deviceId)}
        onClose={onClose}
        title={device?.device_name ?? "Device Details"}
        description={device ? device.registered_device_id : undefined}
      >
        {isLoading ? (
          <div className="space-y-4" aria-busy="true">
            <Skeleton className="h-6 w-28 rounded-full" />
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        ) : isError || !device ? (
          <div className="rounded-xl border border-danger/20 bg-danger-soft p-5 text-center text-danger">
            <AlertCircle className="mx-auto h-7 w-7" aria-hidden="true" />
            <p className="mt-2 text-sm font-semibold">Failed to load device</p>
            <p className="mt-1 text-xs">
              {(error as AppError | null)?.errorDetails ?? "Something went wrong."}
            </p>
            {(error as AppError | null)?.requestId && (
              <p className="mt-2 font-mono text-[11px] text-muted-foreground break-all">
                Request ID: {(error as AppError).requestId}
              </p>
            )}
            <Button variant="outline" size="sm" className="mt-3 bg-card text-foreground" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {actionError && (
              <div role="alert" className="flex items-start gap-2 p-3 text-xs rounded-lg bg-danger-soft text-danger border border-danger/20">
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                <span className="break-words min-w-0">{actionError}</span>
              </div>
            )}

            {/* Status & Action Bar */}
            <div className="flex items-center justify-between gap-3">
              <DeviceStatusBadge status={device.status} />
              <div className="flex items-center gap-2">
                {decommissioned ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={onReactivate}
                    disabled={reactivateMutation.isPending}
                    className="h-9 gap-1.5 text-xs"
                  >
                    {reactivateMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RotateCcw className="h-4 w-4" />
                    )}
                    Reactivate
                  </Button>
                ) : (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        onClose();
                        navigate(`/s-admin/devices/${device.id}/telemetry`);
                      }}
                      className="h-9 gap-1.5 text-xs"
                      title="View live telemetry, CPU/Memory gauges and streaming logs"
                    >
                      <Activity className="h-3.5 w-3.5 text-emerald-500" />
                      Telemetry & Live Logs
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        if (onEdit) {
                          onClose();
                          onEdit(device as DeviceItem);
                        } else {
                          setEditing(true);
                        }
                      }}
                      className="h-9 gap-1.5 text-xs"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setDecommissionOpen(true)}
                      className="h-9 gap-1.5 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Decommission
                    </Button>
                  </>
                )}
              </div>
            </div>

            {editing ? (
              <form onSubmit={handleSubmit(onSave)} className="space-y-4" noValidate>
                <div className="space-y-1">
                  <label htmlFor="edit_name" className="text-xs font-medium block">Device name</label>
                  <Input
                    id="edit_name"
                    {...register("device_name", {
                      required: "Device name is required.",
                      maxLength: { value: 100, message: "Max 100 characters." },
                      validate: (v) => v.trim().length > 0 || "Device name is required.",
                    })}
                    className="h-10 text-sm bg-card"
                  />
                  {errors.device_name && <p role="alert" className="text-[11px] text-destructive">{errors.device_name.message}</p>}
                </div>

                <div className="space-y-1">
                  <label id="inspect_edit_status_label" className="text-xs font-medium block">Status</label>
                  <Select
                    value={watch("status") || device.status}
                    onValueChange={(val: DeviceStatus) =>
                      setValue("status", val, { shouldDirty: true, shouldValidate: true })
                    }
                  >
                    <SelectTrigger aria-labelledby="inspect_edit_status_label" className="h-10 bg-card rounded-xl">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={device.status}>{statusLabel(device.status)} (current)</SelectItem>
                      {nextStatuses.map((s) => (
                        <SelectItem key={s} value={s}>{statusLabel(s)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {device.device_type === "display_module" && (
                  <fieldset className="space-y-3 rounded-xl border border-border/70 bg-muted/20 p-4">
                    <legend className="px-1.5 text-xs font-semibold">Display module</legend>
                    <div className="space-y-1">
                      <label htmlFor="edit_os_name" className="text-xs font-medium block">OS name</label>
                      <Input id="edit_os_name" {...register("os_name", { required: "OS name is required.", maxLength: { value: 100, message: "Max 100 characters." } })} className="h-10 text-sm bg-card" />
                      {errors.os_name && <p role="alert" className="text-[11px] text-destructive">{errors.os_name.message}</p>}
                    </div>
                    <div className="space-y-1">
                      <label htmlFor="edit_os_version" className="text-xs font-medium block">OS version</label>
                      <Input id="edit_os_version" {...register("os_version", { required: "OS version is required.", maxLength: { value: 50, message: "Max 50 characters." } })} className="h-10 text-sm bg-card" />
                      {errors.os_version && <p role="alert" className="text-[11px] text-destructive">{errors.os_version.message}</p>}
                    </div>
                    <div className="space-y-1">
                      <label htmlFor="edit_os_hash" className="text-xs font-medium block">OS hash</label>
                      <Input
                        id="edit_os_hash"
                        spellCheck={false}
                        {...register("os_hash", {
                          required: "OS hash is required.",
                          maxLength: { value: 128, message: "Max 128 characters." },
                          validate: (v) => OS_HASH_REGEX.test(v.trim()) || "Use letters, digits and . _ : + - only.",
                        })}
                        className="h-10 text-sm font-mono bg-card"
                      />
                      {errors.os_hash && <p role="alert" className="text-[11px] text-destructive">{errors.os_hash.message}</p>}
                    </div>
                    <div className="space-y-1">
                      <label htmlFor="edit_notes" className="text-xs font-medium block">Upgrade note (optional)</label>
                      <Input id="edit_notes" maxLength={500} placeholder="e.g. OTA release 1.4.2" {...register("notes")} className="h-10 text-sm bg-card" />
                    </div>
                  </fieldset>
                )}

                <div className="flex gap-2 justify-end pt-2">
                  <Button type="button" variant="outline" onClick={() => setEditing(false)} disabled={updateMutation.isPending}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={updateMutation.isPending} className="min-w-[110px]">
                    {updateMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
                  </Button>
                </div>
              </form>
            ) : (
              <>
                {/* Hardware Identity */}
                <section aria-labelledby="hw-identity">
                  <h3 id="hw-identity" className="text-xs font-semibold text-foreground uppercase tracking-wider mb-2">
                    Hardware Identity
                  </h3>
                  <div className="rounded-xl border border-border/80 bg-card p-3 space-y-1">
                    <Row label="MAC Address">
                      {device.mac_address ? (
                        <span className="inline-flex items-center gap-1.5 font-mono">
                          <span>{device.mac_address}</span>
                          <button
                            type="button"
                            onClick={() => copyText(device.mac_address!, "mac")}
                            aria-label="Copy MAC address"
                            className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                          >
                            {copiedMac ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
                          </button>
                        </span>
                      ) : (
                        <span className="text-muted-foreground/40 font-mono">—</span>
                      )}
                    </Row>
                    <Row label="Registered ID">
                      <span className="inline-flex items-center gap-1.5 font-mono">
                        <span>{device.registered_device_id}</span>
                        <button
                          type="button"
                          onClick={() => copyText(device.registered_device_id, "id")}
                          aria-label="Copy Registered ID"
                          className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                        >
                          {copiedId ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
                        </button>
                      </span>
                    </Row>
                    <Row label="Device Type">
                      <span className="capitalize">{device.device_type.replace("_", " ")}</span>
                    </Row>
                    <Row label="Last Seen">
                      <span>{device.last_seen_at ? formatDateTime(device.last_seen_at) : "Never"}</span>
                    </Row>
                  </div>
                </section>

                {/* Subtype Attributes */}
                {device.display_module && (
                  <section aria-labelledby="type-attrs">
                    <h3 id="type-attrs" className="text-xs font-semibold text-foreground uppercase tracking-wider mb-2">
                      Display Module Firmware
                    </h3>
                    <div className="rounded-xl border border-border/80 bg-card p-3 space-y-1">
                      <Row label="OS Name">{device.display_module.os_name}</Row>
                      <Row label="OS Version">
                        <span className="font-mono font-medium">{device.display_module.os_version}</span>
                      </Row>
                      <Row label="OS Hash">
                        <span className="font-mono text-[11px] truncate block max-w-[200px]" title={device.display_module.os_hash}>
                          {device.display_module.os_hash}
                        </span>
                      </Row>
                    </div>
                  </section>
                )}

                {/* Firmware History */}
                {device.device_type === "display_module" && (
                  <section aria-labelledby="fw-history">
                    <h3 id="fw-history" className="text-xs font-semibold text-foreground uppercase tracking-wider mb-2">
                      Firmware Upgrade History
                    </h3>
                    <div className="rounded-xl border border-border/80 bg-card p-4">
                      <FirmwareTimeline entries={device.firmware_history ?? []} />
                    </div>
                  </section>
                )}

                {/* Security Credentials */}
                <section aria-labelledby="sec-creds">
                  <h3 id="sec-creds" className="text-xs font-semibold text-foreground uppercase tracking-wider mb-2">
                    Security Credentials & Integration
                  </h3>
                  <div className="rounded-xl border border-border/80 bg-card p-4">
                    <DeviceCredentialsCard
                      deviceId={device.id}
                      deviceName={device.device_name}
                      disabled={decommissioned}
                    />
                  </div>
                </section>

                {/* Audit Trail */}
                <section aria-labelledby="audit">
                  <h3 id="audit" className="text-xs font-semibold text-foreground uppercase tracking-wider mb-2">
                    Audit Trail
                  </h3>
                  <div className="rounded-xl border border-border/80 bg-card p-3 space-y-1">
                    <Row label="Created">
                      <span>{formatDateTime(device.created_at)}{device.created_by_name ? ` · ${device.created_by_name}` : ""}</span>
                    </Row>
                    <Row label="Updated">
                      <span>{formatDateTime(device.updated_at)}{device.updated_by_name ? ` · ${device.updated_by_name}` : ""}</span>
                    </Row>
                    {device.deleted_at && (
                      <Row label="Decommissioned">
                        <span>{formatDateTime(device.deleted_at)}{device.deleted_by_name ? ` · ${device.deleted_by_name}` : ""}</span>
                      </Row>
                    )}
                  </div>
                </section>

                {/* Actions Footer matching ViewUserModal */}
                <div className="pt-3 flex items-center justify-end gap-2 border-t border-border mt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onClose}
                    className="h-9 px-4 text-xs"
                  >
                    Close
                  </Button>
                  {!decommissioned && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        onClose();
                        navigate(`/s-admin/devices/${device.id}/telemetry`);
                      }}
                      className="h-9 px-4 text-xs gap-1.5"
                    >
                      <Activity className="h-3.5 w-3.5 text-emerald-500" />
                      Telemetry & Live Logs
                    </Button>
                  )}
                  {!decommissioned && onEdit && (
                    <Button
                      type="button"
                      onClick={() => {
                        onClose();
                        onEdit(device as DeviceItem);
                      }}
                      className="h-9 px-4 text-xs shadow-sm bg-zinc-950 text-white hover:bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-950 dark:hover:bg-white"
                    >
                      <Pencil className="mr-1.5 h-3.5 w-3.5" />
                      Edit Device
                    </Button>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </Drawer>

      <DecommissionDialog
        device={device as DeviceItem | null ?? null}
        open={decommissionOpen}
        onClose={() => setDecommissionOpen(false)}
      />
    </>
  );
}
